import { Component, ChangeDetectorRef, Input, OnDestroy, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subject } from 'rxjs/Subject';
import { filter, finalize, switchMap, takeUntil, takeWhile } from 'rxjs/operators';
import { JoyrideService } from 'ngx-joyride';
import { NbDialogService } from '@nebular/theme';

import {
  BasePlan,
  DEFAULT_DURATION,
  FitnessPlan,
  FitnessPlanData,
  MealPlan,
  MealPlanData,
  ProgramTemplateData,
  TemplatePlan,
} from 'app/@core/interfaces/common/CalorieFriend/plan';
import { Patient, PatientData, PatientProgramViewType } from 'app/@core/interfaces/common/CalorieFriend/patients';
import { TutorialData, TutorialIDs } from 'app/@core/interfaces/common/CalorieFriend/tutorial';
import { FitnessPlansStore, MealPlansStore } from 'app/@core/stores/plans.store';
import { PatientStore } from 'app/@core/stores/patient.store';

import { PatientCalendarModalComponent } from '../calendar-modal/calendar-modal.component';
import { FitnessPlanDay, MealPlanDay } from 'app/@core/interfaces/common/CalorieFriend/plan-day';
import { ConfirmMessageDialogComponent } from 'app/@components/confirm-message-dialog/confirm-message-dialog.component';
import { DeviceDetectorService } from 'ngx-device-detector';
import { NativeAppService } from '../../../../@core/backend/common/services/native-app.service';
import { ProgramPlans } from 'app/@core/interfaces/common/CalorieFriend/program-template';
import { Utility } from 'app/@core/utils/utility';
import * as moment from 'moment';

@Component({
  selector: 'app-patient-program',
  templateUrl: './patient-program.component.html',
  styleUrls: ['./patient-program.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientProgramComponent implements OnDestroy, OnInit {
  @Input() set isFitness(value: boolean) {
    this._isFitness = value;
  }

  @Input() public isProgramTemplate: boolean;

  get isFitness(): boolean {
    return this._isFitness;
  }

  readonly TutorialIDs = TutorialIDs;
  isWaiting: boolean = true;
  isProgramMode: boolean = false;
  programStartDate: Date = new Date();
  programEndDate: Date = null;
  patient: Patient;
  plans: (MealPlan | FitnessPlan)[] = [];
  programVisibility: any;

  public programTemplateData: TemplatePlan<ProgramPlans>;

  private readonly unsubscribe$: Subject<void> = new Subject<void>();
  public _isFitness: boolean = false;
  private _plansService: FitnessPlanData | MealPlanData = null;
  private _plansStore: FitnessPlansStore | MealPlansStore = null;
  private _patientLoaded: boolean = false;

  constructor(
    public joyrideService: JoyrideService,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private patientService: PatientData,
    private patientStore: PatientStore,
    private fitnessPlansService: FitnessPlanData,
    private fitnessPlansStore: FitnessPlansStore,
    private mealPlansService: MealPlanData,
    private mealPlansStore: MealPlansStore,
    private tutorialService: TutorialData,
    private dialogService: NbDialogService,
    private cdRef: ChangeDetectorRef,
    private deviceService: DeviceDetectorService,
    private nativeApp: NativeAppService,
    private programTemplateService: ProgramTemplateData
  ) {
  }

  ngOnDestroy(): void {
    this._plansStore.destroyPlans();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    this._plansService = this._isFitness ? this.fitnessPlansService : this.mealPlansService;
    this._plansStore = this._isFitness ? this.fitnessPlansStore : this.mealPlansStore;
    if (!this.isProgramTemplate) {
      this.patientStore
        .onPatientChanged()
        .pipe(
          takeWhile(() => !this._patientLoaded),
          takeUntil(this.unsubscribe$),
          filter((patient) => !!patient)
        )
        .subscribe((patient) => {
          this._patientLoaded = true;
          this.patient = patient;
          this.isProgramMode = this._isFitness ? patient.fitnessProgramMode > 0 : patient.mealProgramMode > 0;
          this.programStartDate = new Date(
            this._isFitness ? patient.fitnessProgramStartDate : patient.mealProgramStartDate
          );

          if (patient.programViewType) {
            this.programVisibility = PatientProgramViewType[patient.programViewType].valueOf().toString();
          } else {
            this.programVisibility = '0';
          }

          if (this.programStartDate < new Date(1970, 1, 1)) this.programStartDate = new Date();
          this.loadPlans();
        });
    } else {
      this.isProgramMode = true;
      this.loadPlans();
    }
  }

  private loadPlans(): void {
    if (!this.isProgramTemplate) {
      this.isWaiting = true;
      this.cdRef.detectChanges();
      this._plansService
        .listById(this.patient.id)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((data) => {
          this._plansStore.setPlans(data.items as (MealPlan | FitnessPlan)[]);
          this.plans = data.items;
          this.programEndDate = this.endDate(this.plans?.length - 1);
          this.reorderPlans();
          this.cdRef.markForCheck();
        })
        .add(() => {
          this.isWaiting = false;
          this.cdRef.detectChanges();
          this.cdRef.markForCheck();
        });
    } else {
      this.isWaiting = true;
      this.programTemplateService
        .GetTemplateById(this.activeRoute.snapshot.params.id)
        .subscribe((data) => {
          this.programTemplateData = data;
          if (this.isFitness) {
            this.plans = this.programTemplateData.programPlans?.fitnessPlans;
            this._plansStore.setPlans(this.programTemplateData.programPlans?.fitnessPlans as FitnessPlan[]);
          } else {
            this.plans = this.programTemplateData.programPlans?.mealPlan;
            this._plansStore.setPlans(this.programTemplateData.programPlans?.mealPlan as MealPlan[]);
          }
          this.programStartDate = new Date();
          this.programEndDate = this.plans? this.endDate(this.plans.length - 1): null;
          this.reorderPlans();
          this.cdRef.detectChanges();
          this.cdRef.markForCheck();
        })
        .add(() => {
          this.isWaiting = false;
          this.cdRef.detectChanges();
          this.cdRef.markForCheck();
        });
    }
  }

  reorderPlans() {
    if (!this.isProgramMode) {
      this.plans.sort((p1, p2) => {
        if (p1.active) return -1;
        if (p2.active) return 1;
        return p1.createdDate > p2.createdDate ? -1 : 1;
      });

      //this.plans = this.plans.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
      //this.plans = this.plans.sort((a, b) => Number(b.active) - Number(a.active));
    }
  }

  onProgramToClient(): void {
    if (!this.isProgramMode) return;

    if (
      this.plans
        .filter((x) => x.active)
        .some(
          (plan: FitnessPlan | MealPlan) =>
            (plan.days as any[]).reduce(
              (assignedDays: number, day: FitnessPlanDay | MealPlanDay) => assignedDays + day.dayOfWeek.length,
              0
            ) === 0
        )
    ) {
      this.dialogService.open(ConfirmMessageDialogComponent, {
        closeOnEsc: false,
        closeOnBackdropClick: false,
        context: {
          Title: 'Warning',
          Message:
            'Plans in Program Mode must have at least one week day assigned. Please tap the edit button and assign Plan days to week days (Sunday through Monday).',
          isAlert: true,
        },
      });
      return;
    }

    let currentPlanId = 0;
    if (this.plans?.length > 0) {
      const startDate = new Date(this.programStartDate);
      startDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate.toDateString() === today.toDateString() || startDate > today) currentPlanId = this.plans[0].id;
      if (startDate < today) {
        let i = 0;
        while (i < this.plans.length) {
          startDate.setDate(startDate.getDate() + this.plans[i].duration * 7);
          if (startDate > today) break;
          i++;
        }
        if (i < this.plans.length) currentPlanId = this.plans[i].id;
      }
    }

    this.dialogService.open(PatientCalendarModalComponent, {
      closeOnEsc: false,
      closeOnBackdropClick: false,
      context: {
        patient: this.patient,
        programStartDate: this.programStartDate,
        isFitness: this.isFitness,
        planId: currentPlanId,
      },
    });
  }

  onProgramViewTypeChange(): void {
    if (this.isWaiting) return;
    console.log('Here 2');
    this.isWaiting = true;
    this.cdRef.detectChanges();

    this.patientService
      .update({
        ...this.patient,
        ['programViewType']: this.programVisibility as number,
      })
      .subscribe(() => {
        (this.patient['programViewType'] = this.programVisibility as number),
          this.patientStore.setPatient(this.patient);
        this.cdRef.markForCheck();
        this.loadPlans();
      })
      .add(() => {
        this.isWaiting = false;
        this.cdRef.detectChanges();
      });
  }

  onProgramModeChanged(mode: boolean): void {
    if (this.isWaiting) return;
    console.log('Here 4');
    this.isWaiting = true;
    this.cdRef.detectChanges();

    this.patientService
      .update({
        ...this.patient,
        [`${this._isFitness ? 'fitness' : 'meal'}ProgramMode`]: mode ? 1 : -1,
      })
      .subscribe(() => {
        this.patient[`${this._isFitness ? 'fitness' : 'meal'}ProgramMode`] = mode ? 1 : -1;
        this.patientStore.setPatient(this.patient);
        this.isProgramMode = mode;
        this.cdRef.markForCheck();
        this.loadPlans();
      })
      .add(() => {
        this.isWaiting = false;
        this.cdRef.detectChanges();
      });
  }

  onProgramStartDateChanged(date: Date): void {
    if (this.isWaiting) return;
    console.log('here 5');
    this.isWaiting = true;
    this.cdRef.detectChanges();

    //example....
    //let t = { ...this.patient, ['somevale']: 'test' };

    this.patientService
      .update({
        ...this.patient,
        [`${this._isFitness ? 'fitness' : 'meal'}ProgramStartDate`]: date,
      })
      .subscribe(() => {
        this.patient[`${this._isFitness ? 'fitness' : 'meal'}ProgramStartDate`] = date;
        this.patientStore.setPatient(this.patient);
        this.programStartDate = date;
        if (this.programStartDate < new Date(1970)) this.programStartDate = new Date();
        if (this.plans.length > 0) this.programEndDate = this.endDate(this.plans.length - 1);
      })
      .add(() => {
        this.isWaiting = false;
        this.cdRef.detectChanges();
      });
  }

  planTrackBy(_index: number, plan: BasePlan): number {
    return plan.id;
  }

  startDate(index: number): Date {
    const startDate = new Date(this.programStartDate);
    for (let i = 0; i < index; i++) startDate.setDate(startDate.getDate() + this.plans[i].duration * 7);
    return startDate;
  }
  endDate(index: number): Date {
    const endDate = this.startDate(index);
    endDate.setDate(endDate.getDate() + this.plans[index]?.duration * 7);
    return endDate;
  }

  onChanged(_plan: MealPlan | FitnessPlan, index: number): void {
    this.plans[index] = _plan;
    if (!this.isProgramTemplate) this.programEndDate = this.endDate(this.plans.length - 1);
    this.cdRef.detectChanges();
    this.reorderPlans();
    this.updateTemplate();
  }

  onActiveChanged(_plan: MealPlan | FitnessPlan): void {
    if (!this.isProgramMode) {
      if (_plan.active) {
        this.plans.filter((y) => y.id != _plan.id).forEach((y) => (y.active = false));
      }
      this.reorderPlans();
    }

    this.cdRef.detectChanges();
  }

  onDuplicated(plan: MealPlan | FitnessPlan): void {
    this.plans.push({ ...plan, zOrder: this.plans.length + 1 });
    if (!this.isProgramTemplate) this.programEndDate = this.endDate(this.plans.length - 1);
    this.reorderPlans();
    this.cdRef.markForCheck();
    this.cdRef.detectChanges();
    this.updateTemplate();
  }

  onDeleted(planId: number, index: number): void {
    if (!this.isProgramTemplate) {
      this.plans = this.plans.filter((x) => x.id !== planId).map((plan, i) => ({ ...plan, zOrder: i + 1 }));
    } else {
      this.plans = this.plans.filter((x, i) => i !== index).map((plan, i) => ({ ...plan, zOrder: i + 1 }));
    }
    if (!this.isProgramTemplate) this.programEndDate = this.endDate(this.plans.length - 1);
    this.reorderPlans();
    this.cdRef.markForCheck();
    this.cdRef.detectChanges();
    this.updateTemplate();
  }

  onMoved(step: number, index: number): void {
    if (this.plans.length < 2) return;

    const currentIndex = (index + step + this.plans.length) % this.plans.length;
    if (currentIndex === index) return;

    moveItemInArray(this.plans, index, currentIndex);

    this.isWaiting = true;
    this.cdRef.detectChanges();

    // fix the index after moving
    this.plans = this.plans.map((plan, i) => ({ ...plan, zOrder: i + 1 }));
    if (!this.isProgramTemplate) {
      this._plansService
        .movePlan(this.patient.id, this.plans[currentIndex].id, index, currentIndex)
        .subscribe()
        .add(() => this.loadPlans());
    } else {
      this.updateTemplate();
    }
  }

  updateTemplate(): void {
    if (this.isFitness) this.programTemplateData.programPlans.fitnessPlans = this.plans as FitnessPlan[];
    else this.programTemplateData.programPlans.mealPlan = this.plans as MealPlan[];
    this.programTemplateService
      .UpdateTemplate(this.programTemplateData, false)
      .subscribe()
      .add(() => this.loadPlans());
  }

  onAddPlan(): void {
    if (this.isWaiting) return;
    if (this.isProgramTemplate) {
      if (!this.plans) this.plans = [];
      if(!this.isFitness) {
      this.plans.push({
        id: null,
        days: [
          {
            id: -1,
            meals: [
              {
                id: -1,
                dayID: -1,
                name: 'Meal Name',
                time: '8:00 AM',
                items: null,
              },
            ],
            name: 'Untitled Day',
            notes: '',
            dayOfWeek: [],
            createMeal: true,
            order: 1,
          },
        ],
        name: 'Untitled Meal Plan',
        patientID: null,
        createdDate: new Date().toISOString(),
        description: '',
        notes: '',
        zOrder: this.plans.length + 1,
        duration: DEFAULT_DURATION,
        active: false,
      } as MealPlan);
    }
      else {
        this.plans.push({
          id: null,
          days: [
            {
              id: -1,
              blocks: [
                {
                    "id": -1,
                    "dayID": -1,
                    "name": "Block Name",
                    "blockType": 0,
                    "repetitionCount": 1,
                    "circuitRest": 0,
                    "restEnd": 0,
                    "items": null,
                    "order": 1
                }
            ],
              name: 'Untitled Day',
              notes: '',
              dayOfWeek: [],
              createMeal: true,
              order: 1,
            },
          ],
          name: 'Untitled Fitness Plan',
          patientID: null,
          createdDate:  new Date().toISOString(),
          description: '',
          notes: '',
          zOrder: this.plans.length + 1,
          duration: DEFAULT_DURATION,
          active: false,
        } as FitnessPlan);
      }
      this.cdRef.markForCheck();
      this.cdRef.detectChanges();
      if (this.programTemplateData.programPlans) {
        this.updateTemplate();
      } else {
        this.programTemplateData.programPlans = {
          mealPlan: null,
          fitnessPlans: null,
        };
        this.updateTemplate();
      }
      return;
    }
    const isTutorial = this.joyrideService.isTourInProgress();
    this.joyrideService.closeTour();
    this.tutorialService.save(TutorialIDs.AddNewPlan).subscribe();
    this.router.navigate([
      `/pages/CalorieFriend/${this._isFitness ? 'NewFitnessPlan' : 'NewPlan'}/` +
        this.patient.id.toString() +
        (isTutorial ? '/FromTutorial' : ''),
    ]);
  }

  onPlanDropped(event: CdkDragDrop<FitnessPlan[] | MealPlan[]>) {
    moveItemInArray(this.plans, event.previousIndex, event.currentIndex);

    this.isWaiting = true;
    this.cdRef.detectChanges();
    if (!this.isProgramTemplate) {
      this._plansService
        .movePlan(this.patient.id, this.plans[event.currentIndex].id, event.previousIndex, event.currentIndex)
        .subscribe()
        .add(() => this.loadPlans());
    } else {
      this.plans[event.currentIndex].zOrder = event.currentIndex + 1;
      this.plans[event.previousIndex].zOrder = event.previousIndex + 1;
      this.updateTemplate();
    }
  }

  cdkDelay() {
    if (this.isProgramMode) {
      return this.deviceService.isDesktop() && !this.nativeApp.isFromApp() ? 0 : 200;
    } else {
      return 1000 * 1000;
    }
  }

  disableCDK() {
    if (this.nativeApp.isFromApp()) return true;
    if (!this.deviceService.isDesktop()) return true;
    return false;
  }

  get shouldShowActivePlanWarning(): boolean {
    return this.plans?.length > 0 && !this.plans.filter((y) => y.active)?.length;
  }
}
